/** QuickRouter — Éditeur 2D de la carte (SVG, vue de dessus, Y vers le haut).
 *
 * Molette = zoom autour du curseur ; clic milieu, clic droit ou Espace + glisser = déplacer
 * la vue. Outil Sélection : clic = sélectionner, glisser = déplacer (pas de la grille, Alt =
 * libre), R = tourner, F = retourner. Outil Piste : départ sur une pastille, segments à 45°,
 * V = via + changement de couche, arrivée sur une pastille du même net. Outil Via.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useProjectStore } from '../store/useProjectStore.js';
import { worldPads, courtyardPoly, pointInPoly, polyBox, rectCorners, placePoint, pointSegDist, r4, dist, obstaclePoly } from '../../shared/geom.js';
import { padNetMap, routingStatus, netWidth } from '../../shared/netlist.js';
import * as ops from '../../shared/ops.js';
import { copperLayers, routableLayers, LAYER_COLORS, LAYER_LABELS } from '../../shared/layers.js';

const C = {
  board: { vert: '#0f4d2a', bleu: '#123f7a', rouge: '#7a1616', noir: '#141414', blanc: '#d8d8d2', jaune: '#a88a12', violet: '#4b1f6e' },
  ...LAYER_COLORS, tht: '#d9a520', silkF: '#f2f2f2', silkB: '#9fb4e6', edge: '#f0d000', rat: '#7fd3ff', sel: '#ffffff', drc: '#ff3df5', keepout: '#ff8a00',
};

const snap = (v, g) => Math.round(v / g) * g;
const P = (pts) => pts.map(([x, y]) => `${x},${y}`).join(' ');

/** Contrainte à 45° du segment a → b. */
function snap45(a, b) {
  const dx = b[0] - a[0], dy = b[1] - a[1];
  const ang = Math.round(Math.atan2(dy, dx) / (Math.PI / 4)) * (Math.PI / 4);
  const len = Math.hypot(dx, dy) * Math.abs(Math.cos(Math.atan2(dy, dx) - ang));
  return [r4(a[0] + Math.cos(ang) * len), r4(a[1] + Math.sin(ang) * len)];
}

function PadEl({ p, fill, opacity = 1 }) {
  const [w, h] = p.size;
  if (p.shape === 'circle') return <circle cx={p.pos[0]} cy={p.pos[1]} r={Math.max(w, h) / 2} fill={fill} opacity={opacity} />;
  if (p.shape === 'oval' || p.shape === 'roundrect') {
    const r = p.shape === 'oval' ? Math.min(w, h) / 2 : Math.min(w, h) * 0.25;
    return <rect x={p.pos[0] - w / 2} y={p.pos[1] - h / 2} width={w} height={h} rx={r} fill={fill} opacity={opacity} transform={p.rot ? `rotate(${p.rot} ${p.pos[0]} ${p.pos[1]})` : undefined} />;
  }
  return <polygon points={P(rectCorners(p.pos, p.size, p.rot))} fill={fill} opacity={opacity} />;
}

/** Texte lisible dans un repère retourné (Y vers le haut). */
function Label({ x, y, size, children, fill = '#fff', anchor = 'middle', weight = 600 }) {
  return (
    <text x={x} y={-y} transform="scale(1 -1)" fontSize={size} fill={fill} textAnchor={anchor} dominantBaseline="central" fontFamily="Barlow Condensed, sans-serif" fontWeight={weight} style={{ pointerEvents: 'none' }}>
      {children}
    </text>
  );
}

/** Carte mère en fantôme sous la carte fille : contour et obstacles (hauteur en mm). */
function Mother({ mother, k }) {
  return (
    <g style={{ pointerEvents: 'none' }}>
      <polygon points={P(mother.outline)} fill="#1d3a66" fillOpacity={0.35} stroke="#7d9bd0" strokeWidth={1.2 / k} strokeDasharray={`${6 / k} ${4 / k}`} />
      <Label x={polyBox(mother.outline)[0] + 1} y={polyBox(mother.outline)[3] - 1.5} size={Math.max(1.2, 13 / k)} fill="#9db6e0" anchor="start">{mother.name} {mother.side === 'above' ? 'au-dessus (carte porteuse)' : 'dessous'} — {mother.gap} mm entre les cartes</Label>
      {mother.obstacles.map((o, i) => {
        const poly = obstaclePoly(o);
        const b = polyBox(poly);
        return (
          <g key={i}>
            <polygon points={P(poly)} fill="url(#hatch)" stroke="#a9b4c4" strokeWidth={1 / k} strokeOpacity={0.8} />
            <Label x={(b[0] + b[2]) / 2} y={(b[1] + b[3]) / 2} size={Math.max(0.8, Math.min(2.2, 10 / k))} fill="#e3e9f2" weight={500}>{o.label.split(' (')[0]} · {o.height} mm</Label>
          </g>
        );
      })}
    </g>
  );
}

export default function Board2D() {
  const project = useProjectStore((s) => s.project);
  const selection = useProjectStore((s) => s.selection);
  const tool = useProjectStore((s) => s.tool);
  const layer = useProjectStore((s) => s.layer);
  const show = useProjectStore((s) => s.show);
  const highlightNet = useProjectStore((s) => s.highlightNet);
  const fitNonce = useProjectStore((s) => s.fitNonce);
  const drc = useProjectStore((s) => s.drc);
  const st = useProjectStore.getState;

  const svgRef = useRef(null);
  const [size, setSize] = useState([800, 600]);
  const [view, setView] = useState({ x: 0, y: 0, k: 10 }); // x,y = coin haut-gauche en mm ; k = px/mm
  const [cursor, setCursor] = useState(null);
  const [route, setRoute] = useState(null); // { net, layer, width, pts, vias: [] }
  const drag = useRef(null);
  const spaceDown = useRef(false);

  const lookup = st().lookup();
  const pads = useMemo(() => project.components.flatMap((c) => worldPads(c, lookup(c.footprint), copperLayers(project.board)).map((p) => ({ ...p, side: c.side }))), [project.components, project.footprints, project.board.layers]); // eslint-disable-line react-hooks/exhaustive-deps
  const nets = useMemo(() => padNetMap(project), [project.nets]);
  const status = useMemo(() => routingStatus(project, lookup), [project]); // eslint-disable-line react-hooks/exhaustive-deps
  const courts = useMemo(() => project.components.map((c) => ({ c, poly: courtyardPoly(c, lookup(c.footprint)) })).filter((x) => x.poly), [project.components, project.footprints]); // eslint-disable-line react-hooks/exhaustive-deps

  // Taille du conteneur
  useEffect(() => {
    const el = svgRef.current?.parentElement;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => setSize([el.clientWidth, el.clientHeight]));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Cadrage sur la carte
  useEffect(() => {
    const [x0, y0, x1, y1] = polyBox(project.board.outline);
    const k = Math.min(size[0] / (x1 - x0 + 10), size[1] / (y1 - y0 + 10));
    setView({ k, x: (x0 + x1) / 2 - size[0] / 2 / k, y: (y0 + y1) / 2 + size[1] / 2 / k });
  }, [fitNonce, size[0] > 0 && size[1] > 0 ? 1 : 0]); // eslint-disable-line react-hooks/exhaustive-deps

  const toWorld = (e) => {
    const r = svgRef.current.getBoundingClientRect();
    return [view.x + (e.clientX - r.left) / view.k, view.y - (e.clientY - r.top) / view.k];
  };

  const grid = project.rules.grid || 0.25;
  const padAt = (w) => {
    let best = null;
    for (const p of pads) {
      if (!p.layers.length) continue;
      const r = Math.max(...p.size) / 2;
      const d = dist(w, p.pos);
      if (d <= r && (!best || d < best.d)) best = { p, d };
    }
    return best?.p ?? null;
  };
  const trackAt = (w) => {
    let best = null;
    for (const t of project.tracks) {
      if (show[t.layer] === false) continue;
      for (let i = 1; i < t.pts.length; i++) {
        const d = pointSegDist(w, t.pts[i - 1], t.pts[i]);
        if (d <= Math.max(t.width / 2, 3 / view.k) && (!best || d < best.d)) best = { t, d };
      }
    }
    return best?.t ?? null;
  };
  const viaAt = (w) => project.vias.find((v) => dist(w, v.pos) <= v.diameter / 2) ?? null;
  const compAt = (w) => {
    const hits = courts.filter(({ c, poly }) => (show[c.side === 'bottom' ? 'B' : 'F'] || c.locked) && pointInPoly(w, poly));
    hits.sort((a, b) => { const A = polyBox(a.poly), B = polyBox(b.poly); return (A[2] - A[0]) * (A[3] - A[1]) - (B[2] - B[0]) * (B[3] - B[1]); });
    return hits[0]?.c ?? null;
  };

  // ── Outil piste ──
  const routeNext = () => (route && cursor ? snap45(route.pts[route.pts.length - 1], [snap(cursor[0], grid), snap(cursor[1], grid)]) : null);
  const finishRoute = (endPt) => {
    if (!route) return;
    const pts = endPt ? [...route.pts, endPt] : route.pts;
    const segs = route.segs.concat([{ layer: route.layer, pts }]).filter((s) => s.pts.length >= 2);
    st().mutate((p) => {
      for (const s of segs) ops.addTrack(p, { net: route.net, layer: s.layer, width: route.width, pts: dedupe(s.pts) });
      for (const v of route.vias) ops.addVia(p, { net: route.net, pos: v });
    });
    setRoute(null);
  };
  const dedupe = (pts) => pts.filter((q, i) => i === 0 || dist(q, pts[i - 1]) > 1e-6);

  useEffect(() => {
    const onKey = (e) => {
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.code === 'Space') spaceDown.current = e.type === 'keydown';
      if (e.type !== 'keydown') return;
      if (route && e.key === 'Escape') { setRoute(null); e.stopPropagation(); return; }
      if (route && (e.key === 'v' || e.key === 'V')) {
        const at = route.pts[route.pts.length - 1];
        // V : couche de signal suivante (F → In2 → B → F… en 6 couches), via traversant.
        const sig = routableLayers(project.board);
        const nl = sig[(sig.indexOf(route.layer) + 1) % sig.length] ?? (route.layer === 'F' ? 'B' : 'F');
        setRoute({ ...route, segs: [...route.segs, { layer: route.layer, pts: route.pts }], pts: [at], layer: nl, vias: [...route.vias, at] });
        st().setLayer(nl);
        e.stopPropagation();
      }
      if (route && e.key === 'Enter') finishRoute(null);
      if (route && e.key === 'Backspace' && route.pts.length > 1) setRoute({ ...route, pts: route.pts.slice(0, -1) });
    };
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('keyup', onKey, true);
    return () => { window.removeEventListener('keydown', onKey, true); window.removeEventListener('keyup', onKey, true); };
  }, [route]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (tool !== 'track') setRoute(null); }, [tool]);

  // ── Souris ──
  const onWheel = (e) => {
    const w = toWorld(e);
    const k = Math.min(400, Math.max(1, view.k * (e.deltaY < 0 ? 1.18 : 1 / 1.18)));
    const r = svgRef.current.getBoundingClientRect();
    setView({ k, x: w[0] - (e.clientX - r.left) / k, y: w[1] + (e.clientY - r.top) / k });
  };

  const onDown = (e) => {
    if (e.button === 1 || e.button === 2 || spaceDown.current) {
      drag.current = { kind: 'pan', sx: e.clientX, sy: e.clientY, view };
      return;
    }
    if (e.button !== 0) return;
    const w = toWorld(e);
    if (tool === 'track') {
      const pad = padAt(w);
      if (!route) {
        const startNet = pad ? nets.get(pad.key) : (trackAt(w)?.net ?? viaAt(w)?.net);
        if (!startNet) { st().setStatus(pad ? `${pad.key} n'appartient à aucun net : reliez-la d'abord (onglet Nets)` : 'Commencez la piste sur une pastille, une piste ou un via'); return; }
        const start = pad ? pad.pos : [snap(w[0], grid), snap(w[1], grid)];
        const l = pad && pad.layers.length === 1 ? pad.layers[0] : layer;
        if (l !== layer) st().setLayer(l);
        setRoute({ net: startNet, layer: l, width: netWidth(project, startNet, l), pts: [start], segs: [], vias: [] });
        st().setStatus(`Piste ${startNet} sur ${LAYER_LABELS[l] ?? l} — V via et couche suivante, Entrée ou double-clic pour finir, Échap annule`);
        return;
      }
      if (pad && nets.get(pad.key) === route.net && route.pts.length >= 1) {
        const last = route.pts[route.pts.length - 1];
        const mid = snap45(last, pad.pos);
        finishRoute(dist(mid, pad.pos) > 1e-3 ? null : pad.pos);
        if (dist(mid, pad.pos) > 1e-3) {
          st().mutate((p) => {
            const pts = dedupe([...route.pts, mid, pad.pos]);
            for (const s of route.segs) ops.addTrack(p, { net: route.net, layer: s.layer, width: route.width, pts: dedupe(s.pts) });
            ops.addTrack(p, { net: route.net, layer: route.layer, width: route.width, pts });
            for (const v of route.vias) ops.addVia(p, { net: route.net, pos: v });
          });
          setRoute(null);
        }
        return;
      }
      const nxt = routeNext();
      if (nxt) setRoute({ ...route, pts: [...route.pts, nxt] });
      return;
    }
    if (tool === 'via') {
      const t = trackAt(w);
      const pad = padAt(w);
      const net = t?.net ?? (pad ? nets.get(pad.key) : null);
      if (!net) { st().setStatus('Cliquez sur une piste ou une pastille reliée à un net'); return; }
      st().mutate((p) => ops.addVia(p, { net, pos: pad ? pad.pos : [snap(w[0], grid), snap(w[1], grid)] }));
      return;
    }
    // Sélection
    const via = viaAt(w);
    const trk = !via && trackAt(w);
    const comp = !via && !trk && compAt(w);
    const id = via?.id ?? trk?.id ?? comp?.id ?? null;
    const additive = e.shiftKey || e.ctrlKey;
    if (!id) { if (!additive) st().setSelection([]); st().setHighlightNet(null); return; }
    if (!selection.includes(id)) st().select(id, additive);
    else if (additive) { st().select(id, true); return; }
    if (trk) st().setHighlightNet(trk.net);
    if (via) st().setHighlightNet(via.net);
    if (comp && !comp.locked) {
      const ids = new Set(selection.includes(id) ? selection : [id]);
      const starts = Object.fromEntries(project.components.filter((c) => ids.has(c.id) && !c.locked).map((c) => [c.id, c.pos]));
      drag.current = { kind: 'move', start: w, starts, began: false };
    } else if (comp?.locked) st().setStatus(`${comp.ref} est imposé par le gabarit de carte (connecteur de la carte mère)`);
  };

  const onMove = (e) => {
    const w = toWorld(e);
    setCursor(w);
    const d = drag.current;
    if (d?.kind === 'pan') {
      setView({ ...d.view, x: d.view.x - (e.clientX - d.sx) / view.k, y: d.view.y + (e.clientY - d.sy) / view.k });
      return;
    }
    if (d?.kind === 'move') {
      if (!d.began) {
        if (dist(w, d.start) * view.k < 3) return;
        if (!st().beginTransient()) { drag.current = null; return; }
        d.began = true;
      }
      const g = e.altKey ? 0.01 : grid;
      const dx = snap(w[0] - d.start[0], g), dy = snap(w[1] - d.start[1], g);
      st().applyTransient((p) => {
        for (const c of p.components) if (d.starts[c.id]) c.pos = [r4(d.starts[c.id][0] + dx), r4(d.starts[c.id][1] + dy)];
      });
      return;
    }
    if (tool === 'select' || tool === 'track') {
      const pad = padAt(w);
      if (pad) st().setStatus(`${pad.key}${pad.name ? ` (${pad.name})` : ''}${nets.get(pad.key) ? ` → ${nets.get(pad.key)}` : ' — sans net'}  ·  ${w[0].toFixed(2)} ; ${w[1].toFixed(2)} mm`);
    }
  };

  const onUp = () => {
    if (drag.current?.kind === 'move' && drag.current.began) st().commitTransient();
    drag.current = null;
  };

  // ── Rendu ──
  const k = view.k;
  const hiNet = highlightNet;
  const selSet = new Set(selection);
  const dim = (net) => (hiNet && net !== hiNet ? 0.3 : 1);
  // Dessous d'abord, dessus en dernier ; les couches internes entre les deux.
  const order = [...copperLayers(project.board)].reverse();
  const visible = (L) => show[L] !== false;
  const preview = routeNext();

  return (
    <svg
      ref={svgRef}
      className="board2d"
      width={size[0]}
      height={size[1]}
      onWheel={onWheel}
      onMouseDown={onDown}
      onMouseMove={onMove}
      onMouseUp={onUp}
      onMouseLeave={onUp}
      onDoubleClick={() => { if (route) finishRoute(null); }}
      onContextMenu={(e) => e.preventDefault()}
      style={{ cursor: tool === 'select' ? 'default' : 'crosshair' }}
    >
      <defs>
        <pattern id="hatch" width="2" height="2" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width="2" height="2" fill="#b8c2d0" fillOpacity="0.16" />
          <line x1="0" y1="0" x2="0" y2="2" stroke="#b8c2d0" strokeOpacity="0.55" strokeWidth="0.35" />
        </pattern>
        <pattern id="grid" width={2.54 * k} height={2.54 * k} patternUnits="userSpaceOnUse" x={(-view.x * k) % (2.54 * k)} y={(view.y * k) % (2.54 * k)}>
          <circle cx={1} cy={1} r={0.7} fill="#ffffff22" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#grid)" />
      <g transform={`matrix(${k} 0 0 ${-k} ${-view.x * k} ${view.y * k})`}>
        {project.board.mother && <Mother mother={project.board.mother} k={k} />}
        <polygon points={P(project.board.outline)} fill={C.board[project.board.color] ?? C.board.vert} fillOpacity={project.board.mother ? 0.88 : 1} stroke={C.edge} strokeWidth={Math.max(0.1, 1.2 / k)} />

        {show.zones && project.zones.filter((z) => visible(z.layer)).map((z) => (
          <polygon key={z.id} points={P(z.outline ?? project.board.outline)} fill={C[z.layer]} fillOpacity={0.16 * dim(z.net)} stroke={C[z.layer]} strokeWidth={0.12} strokeDasharray="0.8 0.5"
            onMouseDown={undefined} className={selSet.has(z.id) ? 'sel' : ''} />
        ))}

        {/* Keepouts */}
        {courts.map(({ c }) => (lookup(c.footprint)?.keepouts ?? []).map((ko, i) => {
          const kp = [[ko.min[0], ko.min[1]], [ko.max[0], ko.min[1]], [ko.max[0], ko.max[1]], [ko.min[0], ko.max[1]]].map((pt) => placePoint(c, pt));
          return <polygon key={`${c.id}-ko${i}`} points={P(kp)} fill={C.keepout} fillOpacity={0.12} stroke={C.keepout} strokeWidth={0.12} strokeDasharray="0.6 0.4" style={{ pointerEvents: 'none' }} />;
        }))}

        {order.filter(visible).map((L) => (
          <g key={L} opacity={L === layer ? 1 : 0.75}>
            {project.tracks.filter((t) => t.layer === L).map((t) => (
              <polyline key={t.id} points={P(t.pts)} fill="none" stroke={selSet.has(t.id) ? '#fff' : C[L]} strokeOpacity={0.92 * dim(t.net)} strokeWidth={t.width} strokeLinecap="round" strokeLinejoin="round" />
            ))}
            {pads.filter((p) => p.layers.length === 1 && p.layers[0] === L).map((p) => (
              <PadEl key={`${p.key}-${p.idx}`} p={p} fill={L === 'F' ? '#e05a4f' : '#5b8fe8'} opacity={dim(nets.get(p.key))} />
            ))}
          </g>
        ))}

        {/* Traversants et vias */}
        {pads.filter((p) => p.drill).map((p) => (
          <g key={`${p.key}-${p.idx}`} opacity={dim(nets.get(p.key))}>
            {p.layers.length > 0 && <PadEl p={p} fill={C.tht} />}
            <circle cx={p.pos[0]} cy={p.pos[1]} r={p.drill / 2} fill="#111" />
          </g>
        ))}
        {project.vias.map((v) => (
          <g key={v.id} opacity={dim(v.net)}>
            <circle cx={v.pos[0]} cy={v.pos[1]} r={v.diameter / 2} fill={selSet.has(v.id) ? '#fff' : '#c9c9c9'} />
            <circle cx={v.pos[0]} cy={v.pos[1]} r={v.drill / 2} fill="#111" />
          </g>
        ))}

        {/* Sérigraphie, repères, sélection */}
        {show.silk && courts.map(({ c, poly }) => {
          const fp = lookup(c.footprint);
          const col = c.side === 'bottom' ? C.silkB : C.silkF;
          if (!show[c.side === 'bottom' ? 'B' : 'F'] && !fp.pads.some((p) => p.drill)) return null;
          const b = polyBox(poly);
          return (
            <g key={c.id} style={{ pointerEvents: 'none' }}>
              {fp.silk.map((g, i) => {
                const pts = g.pts.map((pt) => placePoint(c, pt));
                if (g.kind === 'circle') return <circle key={i} cx={pts[0][0]} cy={pts[0][1]} r={g.r ?? 0.3} fill={g.fill ? col : 'none'} stroke={col} strokeWidth={g.width} />;
                return <polyline key={i} points={P(pts)} fill="none" stroke={col} strokeWidth={g.width} />;
              })}
              <Label x={(b[0] + b[2]) / 2} y={b[3] + 0.9} size={Math.max(0.9, Math.min(1.6, 11 / k))} fill={c.locked ? '#ffe08a' : col}>{c.ref}</Label>
            </g>
          );
        })}
        {courts.filter(({ c }) => selSet.has(c.id)).map(({ c, poly }) => (
          <polygon key={`sel-${c.id}`} points={P(poly)} fill="#ffffff10" stroke={C.sel} strokeWidth={1.5 / k} strokeDasharray={`${4 / k} ${3 / k}`} style={{ pointerEvents: 'none' }} />
        ))}
        {show.pinNames && k > 9 && pads.filter((p) => p.layers.length && (show[p.layers[0]] || p.drill)).map((p) => {
          const label = nets.get(p.key) || p.name;
          if (!label) return null;
          const s = Math.max(0.3, Math.min(0.75, Math.min(...p.size) * 0.42));
          return <Label key={`n-${p.key}-${p.idx}`} x={p.pos[0]} y={p.pos[1]} size={s} fill={nets.get(p.key) ? '#111' : '#5a3d00'} weight={500}>{label.slice(0, 7)}</Label>;
        })}
        {show.silk && project.texts.filter((t) => show[t.layer]).map((t) => (
          <g key={t.id} transform={`rotate(${t.rot} ${t.pos[0]} ${t.pos[1]})`}>
            <Label x={t.pos[0]} y={t.pos[1]} size={t.size * 1.35} fill={selSet.has(t.id) ? '#fff' : t.layer === 'B' ? C.silkB : C.silkF}>{t.text}</Label>
          </g>
        ))}

        {show.rats && status.nets.flatMap((n) => n.rats.map((r, i) => (
          <line key={`${n.name}-${i}`} x1={r.a[0]} y1={r.a[1]} x2={r.b[0]} y2={r.b[1]} stroke={C.rat} strokeWidth={1.2 / k} strokeOpacity={dim(n.name)} style={{ pointerEvents: 'none' }} />
        )))}

        {show.drc && drc?.errors.filter((e) => e.at).map((e, i) => (
          <circle key={`d${i}`} cx={e.at[0]} cy={e.at[1]} r={6 / k} fill="none" stroke={C.drc} strokeWidth={2 / k} style={{ pointerEvents: 'none' }}><title>{e.message}</title></circle>
        ))}

        {/* Piste en cours */}
        {route && (
          <g style={{ pointerEvents: 'none' }}>
            {route.segs.map((s, i) => <polyline key={i} points={P(s.pts)} fill="none" stroke={C[s.layer]} strokeWidth={route.width} strokeLinecap="round" strokeLinejoin="round" />)}
            <polyline points={P(preview ? [...route.pts, preview] : route.pts)} fill="none" stroke={C[route.layer]} strokeOpacity={0.85} strokeWidth={route.width} strokeLinecap="round" strokeLinejoin="round" />
            {route.vias.map((v, i) => <circle key={i} cx={v[0]} cy={v[1]} r={project.rules.viaDiameter / 2} fill="#c9c9c9" />)}
          </g>
        )}
      </g>
      {cursor && <text x={8} y={size[1] - 8} className="coords">{cursor[0].toFixed(2)} ; {cursor[1].toFixed(2)} mm · {tool === 'track' ? `couche ${layer}` : ''}</text>}
    </svg>
  );
}
